import { useTranslation } from 'react-i18next'
import { useCallback, useMemo } from 'react'
import { Popover, PopoverArrow, PopoverContent, PopoverTrigger } from '../common/popover'
import { cn } from 'src/lib/utils'
import { LANGUAGES } from 'src/i18n/config'
import { ChevronDown } from 'lucide-react'
import i18next from 'i18next'

const getLocaleDisplayName = (locale: string, displayLocale?: string) => {
  const displayName = new Intl.DisplayNames([displayLocale || locale], {
    type: 'language',
  }).of(locale)!
  return displayName.charAt(0).toLocaleUpperCase() + displayName.slice(1)
}

// Flag components for different languages
const FlagIcon = ({ locale }: { locale: string }) => {
  const flagMap: Record<string, string> = {
    en: '🇺🇸',
    de: '🇩🇪',
  }
  
  return (
    <span className="text-lg" role="img" aria-label={`${locale} flag`}>
      {flagMap[locale] || '🌐'}
    </span>
  )
}

const LanguageSelector = () => {
  const { i18n } = useTranslation()

  const localesAndNames = useMemo(() => {
    return LANGUAGES.map((locale) => ({
      locale,
      name: getLocaleDisplayName(locale),
    }))
  }, [])

  const languageChanged = useCallback(async (locale: any) => {
    i18next.changeLanguage(locale)
  }, [])

  const { resolvedLanguage: currentLanguage } = i18n

  return (
    <div className="flex items-center">
      <Popover>
        <PopoverTrigger asChild>
          <button className="flex items-center gap-2 px-3 py-2 rounded-md border border-gray-200 bg-white hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
            <FlagIcon locale={currentLanguage || 'en'} />
            <span className="text-sm font-medium text-gray-700">
              {currentLanguage && getLocaleDisplayName(currentLanguage)}
            </span>
            <ChevronDown size={14} className="text-gray-500" />
          </button>
        </PopoverTrigger>

        <PopoverContent 
          align="end" 
          side="bottom"
          className="w-48 p-1 bg-white border border-gray-200 rounded-lg shadow-lg"
        >
          {localesAndNames.map(({ locale, name }) => {
            const isSelected = currentLanguage === locale
            return (
              <div
                key={locale}
                onClick={() => languageChanged(locale)}
                className={cn(
                  "flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer transition-colors duration-150",
                  isSelected 
                    ? "bg-blue-50 text-blue-700" 
                    : "text-gray-700 hover:bg-gray-50"
                )}
              >
                <FlagIcon locale={locale} />
                <span className={cn(
                  "text-sm font-medium",
                  isSelected && "font-semibold"
                )}>
                  {name}
                </span>
                {isSelected && (
                  <div className="ml-auto w-2 h-2 bg-blue-500 rounded-full"></div>
                )}
              </div>
            )
          })}
        </PopoverContent>
      </Popover>
    </div>
  )
}

export { LanguageSelector }
